/**
 * Modal de criação de campanha.
 *
 * Obrigacional por padrão porque é o que estas campanhas são: aviso de que a
 * empresa tem uma obrigação legal de publicar. Não é oferta. A distinção
 * decide se a peça sai ou não durante a janela eleitoral. O público nasce
 * apontado para o segmento escolhido — quem cobra colégios não quer a base
 * inteira por engano.
 */

import { SEGMENTOS } from '@egba-do/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api, type Categoria, type Item, type Lista } from '../../api/cliente'
import { Aviso, Botao, Campo, Modal, Selecao } from '../../componentes/ui'
import type { Campanha } from './tipos'

export function NovaCampanha({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const qc = useQueryClient()
  const navegar = useNavigate()
  const [erro, setErro] = useState<string | null>(null)
  const [form, setForm] = useState({
    nome: '',
    objetivo: '',
    categoriaId: '',
    segmento: 'escola_particular',
    classeComunicacao: 'obrigacional',
  })

  const { data: categorias } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.get<Lista<Categoria>>('/api/v1/admin/categorias'),
  })

  const criar = useMutation({
    mutationFn: () =>
      api.post<Item<Campanha>>('/api/v1/campanhas', {
        nome: form.nome,
        objetivo: form.objetivo || undefined,
        categoriaId: form.categoriaId || undefined,
        classeComunicacao: form.classeComunicacao,
        publicoAlvo: {
          ...(form.segmento ? { segmento: [form.segmento] } : {}),
          ...(form.categoriaId ? { categoriaId: form.categoriaId } : {}),
          canal: 'email',
        },
      }),
    onSuccess: (r) => {
      aoFechar()
      setForm({
        nome: '',
        objetivo: '',
        categoriaId: '',
        segmento: 'escola_particular',
        classeComunicacao: 'obrigacional',
      })
      void qc.invalidateQueries({ queryKey: ['campanhas'] })
      void navegar(`/campanhas/${r.dados.id}`)
    },
    onError: (e: Error) => setErro(e.message),
  })

  return (
    <Modal aberto={aberto} aoFechar={aoFechar} titulo="Nova campanha">
      <div className="space-y-4">
        {erro && <Aviso tom="perigo">{erro}</Aviso>}
        <Campo
          rotulo="Nome"
          obrigatorio
          value={form.nome}
          onChange={(e) => setForm({ ...form, nome: e.target.value })}
          ajuda="O nome é o que você vai digitar para confirmar o disparo."
        />
        <Campo
          rotulo="Objetivo"
          value={form.objetivo}
          onChange={(e) => setForm({ ...form, objetivo: e.target.value })}
        />
        <Selecao
          rotulo="Quem recebe (segmento)"
          value={form.segmento}
          onChange={(e) => setForm({ ...form, segmento: e.target.value })}
          opcoes={[
            ...SEGMENTOS.map((s) => ({ valor: s.id, texto: s.nome })),
            { valor: '', texto: 'Toda a base (defina os filtros depois)' },
          ]}
          ajuda="Dá para refinar na página da campanha — estágio, canal, categoria."
        />
        <Selecao
          rotulo="Categoria"
          value={form.categoriaId}
          onChange={(e) => setForm({ ...form, categoriaId: e.target.value })}
          opcoes={[
            { valor: '', texto: 'Todas' },
            ...(categorias?.dados ?? []).map((c) => ({ valor: c.id, texto: c.nome })),
          ]}
        />
        <Selecao
          rotulo="Classificação predominante"
          value={form.classeComunicacao}
          onChange={(e) => setForm({ ...form, classeComunicacao: e.target.value })}
          opcoes={[
            { valor: 'obrigacional', texto: 'Obrigacional — avisa sobre uma obrigação legal' },
            { valor: 'transacional', texto: 'Transacional — responde a um ato do destinatário' },
            { valor: 'institucional', texto: 'Institucional — divulga a EGBA' },
            { valor: 'promocional', texto: 'Promocional — oferta ou venda' },
          ]}
          ajuda="Avisar uma escola de que a lei exige a publicação é obrigacional, não promocional — e é o que passa na janela eleitoral."
        />
        <Aviso tom="info">
          Estas campanhas comunicam uma <strong>obrigação legal</strong>: a escola precisa publicar
          a relação de concluintes (Res. CEE nº 44/2020, Art. 4º) e a companhia precisa publicar
          suas demonstrações (Lei 6.404/76, art. 289). Classificar corretamente é o que permite
          avisar durante o período eleitoral.
        </Aviso>
      </div>
      <div className="mt-5 flex justify-end gap-3">
        <Botao variante="secundario" onClick={aoFechar}>
          Cancelar
        </Botao>
        <Botao
          carregando={criar.isPending}
          disabled={form.nome.trim().length < 3 || !form.classeComunicacao}
          onClick={() => criar.mutate()}
        >
          Criar e desenhar a régua
        </Botao>
      </div>
    </Modal>
  )
}
